/* Controls when the newsletter popup is allowed to open.
 *
 * Three gates, in order:
 *   1. Subscribed - the popup and the side tab are removed for good.
 *   2. Seen recently - it opened within the last day, so it stays shut. The
 *      side tab is left in place, which is the only way back to it.
 *   3. Not the home page - it only ever opens itself on the main page.
 *
 * Together these mean it shows once, and a visitor who closes it does not get
 * it again on the next page they click through to. The theme's own popup.js has
 * a view cap but that counts views across a cookie window, which is not the same
 * thing and did not stop the reopen-on-navigate case.
 *
 * Shopify's customer form redirects back with ?customer_posted=true, which is
 * how a signup is detected. That covers the popup form and the footer form.
 */
(function () {
  'use strict';

  /* Testing only - true makes it open every time, ignoring every gate below. */
  var ALWAYS_SHOW = false;

  /* Quiet period after it has been seen. */
  var COOLDOWN_HOURS = 24;

  /* Only let it open itself on the home page. */
  var HOME_ONLY = true;

  var SUBSCRIBED = 'aborder-subscribed';
  var SEEN = 'aborder-popup-seen';
  var TWO_YEARS = 60 * 60 * 24 * 365 * 2;
  var COOLDOWN_MS = COOLDOWN_HOURS * 60 * 60 * 1000;

  function readCookie(name) {
    var parts = document.cookie.split(';');
    for (var i = 0; i < parts.length; i++) {
      var bit = parts[i].replace(/^\s+/, '');
      if (bit.indexOf(name + '=') === 0) return bit.slice(name.length + 1);
    }
    return '';
  }

  function write(name, value, maxAge) {
    try {
      localStorage.setItem(name, value);
    } catch (e) {
      /* private mode - the cookie below is the fallback */
    }
    document.cookie =
      name + '=' + value + '; path=/; max-age=' + maxAge + '; SameSite=Lax';
  }

  function read(name) {
    try {
      var v = localStorage.getItem(name);
      if (v) return v;
    } catch (e) {
      /* fall through to the cookie */
    }
    return readCookie(name);
  }

  function subscribed() {
    return read(SUBSCRIBED) === '1';
  }

  function rememberSubscribed() {
    write(SUBSCRIBED, '1', TWO_YEARS);
  }

  /* Stamped when the popup opens and again when it closes, so the quiet period
     runs from whichever happened last. */
  function markSeen() {
    write(SEEN, String(Date.now()), TWO_YEARS);
  }

  function seenWithinCooldown() {
    var raw = parseInt(read(SEEN), 10);
    if (isNaN(raw) || raw <= 0) return false;
    var age = Date.now() - raw;
    /* A clock change can put the stamp in the future; treat that as stale. */
    if (age < 0) return false;
    return age < COOLDOWN_MS;
  }

  function onHomePage() {
    if (document.body && document.body.classList.contains('template-index')) {
      return true;
    }
    /* Fallback if the body class is ever missing - handles /, /index and a
       locale root like /en-us/. */
    return /^\/(index)?$/.test(window.location.pathname.replace(/^\/[a-z]{2}(-[a-z]{2})?\//i, '/'));
  }

  function newsletterPopup() {
    var popups = document.querySelectorAll('.popup[data-id]');
    for (var i = 0; i < popups.length; i++) {
      if (
        popups[i].querySelector('input[name="contact[email]"]') ||
        popups[i].querySelector('.newsletter-form')
      ) {
        return popups[i];
      }
    }
    return null;
  }

  /* Once subscribed the popup is taken out of the page entirely, before
     popup.js can bind to it. Any other popup on the page is left alone. */
  function silencePopups() {
    var popup = newsletterPopup();
    if (popup && popup.parentNode) popup.parentNode.removeChild(popup);
    var tab = document.getElementById('ap-offer-tab');
    if (tab && tab.parentNode) tab.parentNode.removeChild(tab);
  }

  /* popup.js adds .popup-open on open and drops it on close, so watching the
     class attribute catches every route out - the X, Escape, F4 and a click on
     the overlay - without depending on any of their selectors. */
  function watchOpenState(popup) {
    if (!window.MutationObserver) return;
    var wasOpen = popup.classList.contains('popup-open');
    if (wasOpen) markSeen();

    new MutationObserver(function () {
      var isOpen = popup.classList.contains('popup-open');
      if (isOpen !== wasOpen) {
        wasOpen = isOpen;
        markSeen();
      }
    }).observe(popup, { attributes: true, attributeFilter: ['class'] });
  }

  /* The section only allows one opening trigger, so it is set to "manual" and
     both the timer and the scroll point click the same hidden element -
     whichever gets there first wins. Timings come off the popup's own data
     attributes so the theme editor stays in charge of them. */
  function armTriggers(popup) {
    var hook = document.getElementById('ap-newsletter-trigger');
    if (!hook) return;

    var delay = parseFloat(popup.getAttribute('data-open-delay-number'));
    if (isNaN(delay)) delay = 8;
    var point = parseFloat(popup.getAttribute('data-open-scroll-position'));
    if (isNaN(point)) point = 50;

    var done = false;

    function scrolledPercent() {
      var doc = document.documentElement;
      var max = doc.scrollHeight - doc.clientHeight;
      if (max <= 0) return 0;
      return (window.scrollY / max) * 100;
    }

    function fire() {
      if (done) return;
      done = true;
      window.removeEventListener('scroll', onScroll);
      hook.click();
    }

    function onScroll() {
      if (scrolledPercent() >= point) fire();
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.setTimeout(fire, delay * 1000);
  }

  function init() {
    try {
      if (window.location.search.indexOf('customer_posted=true') !== -1) {
        rememberSubscribed();
      }

      if (!ALWAYS_SHOW && subscribed()) {
        silencePopups();
        return;
      }

      var popup = newsletterPopup();
      if (!popup) return;

      /* Watch it either way - opening it from the side tab should restart the
         quiet period too. */
      watchOpenState(popup);

      if (ALWAYS_SHOW) {
        armTriggers(popup);
        return;
      }

      if (seenWithinCooldown()) return;
      if (HOME_ONLY && !onHomePage()) return;

      armTriggers(popup);
    } catch (e) {
      /* Worst case it simply never auto-opens, which is the safe direction. */
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
